import { mapActions, mapGetters, mapState } from 'vuex';
import composer from '@/composers';
import { IFile, IValidation } from '@/services/asset';
import { IAssetManagerItem, IAssetManagerState, IMessages } from './types';
import store from './store';

/*
  This composition to be used
  when asset uplaoding is required
  via asset manager components

  - HOF component for components
  - HOF wrapper for views
*/

const MB = 1048576;
const storeName = 'asset';

export * from './types';

export const component = (
  view: IView,
) => composer({
  mergeFunction: {
    created: {
      where: 'before',
      cb(this: any) {

        // merge default validation props with component's own
        this.assetComposerValidation = {
          fileLimit: 10,
          fileTypes: ['image/jpeg', 'image/png', 'image/svg+xml'],
          sizeLimit: 10,
          ...this.validation,
        };
      },
    },
  },
  props: {
    record: Object,
    validation: {
      type: Object,
      default: () => ({}),
    },
  },
  computed: {
    ...mapState(storeName, {
      assetComposerAllAssets: 'assets',
    }),
    ...mapGetters(storeName, {
      assetComposerList: 'list',
    }),
    ...mapState(storeName, {
      assetComposerAssets(state: IAssetManagerState) {
        // Requirement where multiple asset manager boxes are in view
        // match asset type id to this box's type id
        const id = (this as any).record.type_id;
        return state.assets.filter(item => item.data.type_id === id);
      },
    }),
    assetComposerUpdate: {
      get() {
        return this.assetComposerAssets;
      },
      set(assets: any) {
        // update with new index
        this.assetComposerUpdateAssets(
          assets.map((item: IAssetManagerItem, index: number) => {
            // update data type as we're using
            // upsert instead of update ... lazy
            item.data.type = this.record.type;
            item.data.index = index;
            return item.data;
          }),
        );
      },
    },
    assetComposerWithinFileLimit() {
      return this.assetComposerAssets.length < this.assetComposerValidation.fileLimit;
    },
  },
  watch: {
    // emit event for uploaded assets
    // why this key? https://stackoverflow.com/a/56461539
    [`$store.state.${storeName}.list`](assets: IAssetManagerItem[]) {
      // this lists all assets regardless of the number of asset managers
      // so filter based on the current asset manager this component is assigned to
      const id = (this as any).record.type_id;
      const uploaded = assets
        .filter(asset => asset.data.type_id === id)
        .map(asset => asset.data);

      uploaded.length
        && this.$emit('upload', uploaded);
    },
  },
  methods: {
    ...mapActions(storeName, {
      assetComposerSyncAssets: 'loadAssets',
      assetComposerRemove: 'remove',
      assetComposerUpdateAssets: 'update',
      assetComposerUploader: 'upload',
      assetComposerUploadError: 'uploadError',
    }),
    assetComposerUpload(files: IFile[]) {
      // check for file limit validation here
      // as uploader only performs validation
      // on each individual file

      const fileArray = Array.from(files);
      const expectedFileCount = this.assetComposerAssets.length + fileArray.length;
      if (expectedFileCount > this.assetComposerValidation.fileLimit) {
        return this.assetComposerUploadError({
          code: 'VALIDATION_ERROR',
          errors: ['MAX_FILELIMIT'],
        });
      }

      this.assetComposerUploader({
        files: fileArray,
        options: {
          record: this.record,
          validation: {
            FILETYPE_RESTRICT: (file: IFile) => (
              this.assetComposerValidation.fileTypes.includes(file.type)
            ),
            MAX_FILESIZE: (file: IFile) => (
              file.size < MB * this.assetComposerValidation.sizeLimit
            ),
          } as IValidation,
        },
      });
    },
  },
} as IView, view);

export const wrapper = (
  view: IView,
  messages: IMessages,
) => composer({
  mergeFunction: {
    created: {
      where: 'before',
      cb(this: any) {

        this.$storeRegister(storeName, store);

        if (!this.$store.state.asset) {
          // create single subscription that handles all errors
          this.$store.subscribe((mutation: any) => {

            // only deal with asset store errors
            const { payload, type } = mutation;
            if (
              type !== `${storeName}/UPLOAD_ERROR`
                && type !== `${storeName}/REMOVE_ERROR`
            ) {
              return;
            }
            const error = payload.error;
            const firstError = error && error.code === 'VALIDATION_ERROR'
              ? { code: error.errors[0] }
              : error;
            const key = `ERROR_UPLOAD_${firstError.code}`;
            const content = messages[key] || messages.ERROR_UPLOAD_UNKNOWN_ERROR;
            this.$store.commit('dialog/CREATE', {
              title: 'Upload error',
              content,
              compact: true,
              buttons: [{
                text: 'Close',
                click: (dialog: any) => dialog.close(),
              }],
            }, {
              root: true,
            });
          });
        }
      },
    },
  },
}, component(view));
